import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Button, FullWidthContainer, PageLoader } from '../../components';
import { useMinimumLoader } from '../../hooks/useMinimumLoader';
import { KnowledgeBaseFormModal } from './components';
import {
  useKnowledgeBase,
  useKnowledgeBaseProAccess,
  useUnansweredQuestions,
  useWebsites,
} from './hooks';
import { CompanyInfoTab } from './tabs/CompanyInfoTab';
import { DocumentsTab } from './tabs/DocumentsTab';
import { FaqTab } from './tabs/FaqTab';
import { SettingsTab } from './tabs/SettingsTab';
import { UnansweredQuestionsTab } from './tabs/UnansweredQuestionsTab';
import { WebsiteTab } from './tabs/WebsiteTab';

type TabId = 'company' | 'documents' | 'faq' | 'website' | 'unanswered' | 'settings';

const TAB_ICONS: Record<TabId, string> = {
  company: 'business',
  documents: 'description',
  faq: 'quiz',
  website: 'language',
  unanswered: 'help_outline',
  settings: 'settings',
};

export const KnowledgeBaseDetail: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  // Remount per knowledge base, so its documents and filters start fresh
  return <KnowledgeBaseDetailContent key={id} knowledgeBaseId={id} />;
};

const KnowledgeBaseDetailContent: React.FC<{ knowledgeBaseId: string }> = ({ knowledgeBaseId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab: string }>();
  const store = useKnowledgeBase(knowledgeBaseId);
  const { hasProAccess, isLoading: proAccessLoading } = useKnowledgeBaseProAccess();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const showLoader = useMinimumLoader(store.initialLoading, { minimumDuration: 500 });
  // Badge on the tab, shares its query with the tab's default filter
  const { data: openQuestions } = useUnansweredQuestions(knowledgeBaseId, 'open', {
    enabled: hasProAccess,
  });
  // Marks on the website tab: a failed import, new pages to select
  const { data: websites = [] } = useWebsites(knowledgeBaseId, { enabled: hasProAccess });
  const websiteImportFailed = websites.some(website => website.crawl_status === 'failed');
  const pendingWebsitePages = websites.reduce(
    (sum, website) => sum + website.pending_page_count,
    0
  );

  // Company info and FAQ first: they take precedence over documents for the agent, too
  const tabs: TabId[] = hasProAccess
    ? ['company', 'faq', 'documents', 'website', 'unanswered', 'settings']
    : ['documents', 'settings'];
  const knowledgeBase = store.knowledgeBases.find(kb => kb.id === knowledgeBaseId);

  const handleCreate = async (data: { name: string; description: string }) => {
    const created = await store.createKnowledgeBase(data);
    if (created) navigate(`/knowledge-base/${created.id}`);
  };

  // Wait for the tier, so a link to a pro tab is not redirected while it loads
  if (proAccessLoading) return <PageLoader show />;

  // Without a tab, or one that is hidden without the tier, open the first visible tab
  if (!tabs.includes(tab as TabId)) {
    return <Navigate to={`/knowledge-base/${knowledgeBaseId}/${tabs[0]}`} replace />;
  }

  if (showLoader) return <PageLoader show />;

  if (store.error || !knowledgeBase) {
    return (
      <FullWidthContainer>
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            {store.error ?? t('knowledgeBase.notFound')}
          </p>
          <Button variant="secondary" onClick={() => navigate('/knowledge-base')}>
            {t('common.back')}
          </Button>
        </div>
      </FullWidthContainer>
    );
  }

  return (
    <FullWidthContainer>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {store.knowledgeBases.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                title={t('knowledgeBase.allKnowledgeBases')}
                onClick={() => navigate('/knowledge-base')}
              >
                <span className="material-icons">arrow_back</span>
              </Button>
            )}
            <div className="min-w-0">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white truncate">
                {knowledgeBase.name}
              </h1>
              {knowledgeBase.description && (
                <p className="text-gray-600 dark:text-gray-400 mt-1 line-clamp-1">
                  {knowledgeBase.description}
                </p>
              )}
              {/* Bookable hours, services and resources live in the agents' calendars */}
              {knowledgeBase.agents.map(agent =>
                agent.schedulingCalendars.map(calendar => (
                  <p
                    key={`${agent.id}-${calendar.id}`}
                    className="mt-1 flex flex-wrap items-center gap-x-1 text-sm text-gray-600 dark:text-gray-400"
                  >
                    <span className="material-icons text-sm">event</span>
                    {t('knowledgeBase.bookingCalendar.books', { agent: agent.name })}
                    <Link
                      to={`/calendars/${calendar.id}`}
                      className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                    >
                      {calendar.name}
                    </Link>
                    <span>· {t('knowledgeBase.bookingCalendar.hint')}</span>
                  </p>
                ))
              )}
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setIsCreateOpen(true)}
            className="flex items-center gap-2 sm:ml-auto shrink-0"
          >
            <span className="material-icons">add</span>
            {t('knowledgeBase.createKnowledgeBase')}
          </Button>
        </div>

        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex gap-6 overflow-x-auto">
            {tabs.map(tabId => (
              <button
                key={tabId}
                type="button"
                onClick={() => navigate(`/knowledge-base/${knowledgeBaseId}/${tabId}`)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 py-3 text-sm font-medium transition-colors ${
                  tab === tabId
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <span className="material-icons text-sm">{TAB_ICONS[tabId]}</span>
                {t(`knowledgeBase.tabs.${tabId}`)}
                {tabId === 'unanswered' && !!openQuestions?.length && (
                  <Badge variant="warning" size="sm">
                    {openQuestions.length}
                  </Badge>
                )}
                {tabId === 'website' && websiteImportFailed && (
                  <Badge variant="error" size="sm">
                    !
                  </Badge>
                )}
                {tabId === 'website' && pendingWebsitePages > 0 && (
                  <Badge variant="info" size="sm">
                    {pendingWebsitePages}
                  </Badge>
                )}
              </button>
            ))}
          </nav>
        </div>

        {tab === 'documents' && <DocumentsTab knowledgeBase={knowledgeBase} store={store} />}
        {tab === 'company' && <CompanyInfoTab knowledgeBase={knowledgeBase} />}
        {tab === 'faq' && <FaqTab knowledgeBase={knowledgeBase} />}
        {tab === 'website' && <WebsiteTab knowledgeBase={knowledgeBase} />}
        {tab === 'unanswered' && <UnansweredQuestionsTab knowledgeBase={knowledgeBase} />}
        {tab === 'settings' && <SettingsTab knowledgeBase={knowledgeBase} store={store} />}

        <KnowledgeBaseFormModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onSubmit={handleCreate}
          knowledgeBase={null}
        />
      </div>
    </FullWidthContainer>
  );
};
